import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Link } from 'react-router-dom';
import clsx from 'clsx';
import {
  Plus, Mail, MessageSquare, Download, Sparkles, AlertTriangle, Users, ShieldAlert,
  PauseCircle, FileX, ChevronDown, ChevronLeft, ChevronRight, SlidersHorizontal, CheckSquare,
  Square, FileSpreadsheet, X, Phone, CheckCircle2, Eye, EyeOff,
} from 'lucide-react';
import api from '../../utils/api';
import useConnectedIntegrations from '../../hooks/useConnectedIntegrations';
import toast from 'react-hot-toast';
import {
  PAGE_WIDE, DataTable, SearchInput, KpiTile, OverflowMenu, BulkBar, Dialog, Field, Tabs,
  EmptyState, ErrorState, TableSkeleton, PersonAvatar, StatusPill, useUrlTab, fmtDate, fmtAgo,
  num, plural,
} from '../../components/ui';
import {
  roleLabel, employmentLabel, genderLabel, ComplianceBadge, useStableCallback,
} from './hrKit';

/* ─── Columns ────────────────────────────────────────────────────────────── */

// Eight columns fit at 1280–1440px; the rest are one click away in the Columns menu.
const DEFAULT_COLUMNS = ['role', 'employment', 'team', 'mobile', 'compliance', 'last_login', 'start_date'];
const OPTIONAL_COLUMNS = [
  { key: 'job_title', label: 'Job title' },
  { key: 'email', label: 'Email' },
  { key: 'gender', label: 'Gender' },
  { key: 'created', label: 'Created' },
  { key: 'hour_cap', label: 'Hour cap' },
];
const PICKABLE = [
  { key: 'role', label: 'Role' },
  { key: 'employment', label: 'Employment' },
  { key: 'team', label: 'Team' },
  { key: 'mobile', label: 'Mobile' },
  { key: 'compliance', label: 'Compliance' },
  { key: 'last_login', label: 'Last login' },
  { key: 'start_date', label: 'Start date' },
  ...OPTIONAL_COLUMNS,
];
const COLS_KEY = 'staffList.columns.v1';
const readColumns = () => {
  try {
    const v = JSON.parse(localStorage.getItem(COLS_KEY) || 'null');
    if (Array.isArray(v) && v.every((k) => PICKABLE.some((p) => p.key === k))) return v;
  } catch { /* storage blocked: fall back to the defaults */ }
  return DEFAULT_COLUMNS;
};

const fullName = (s) => `${s.first_name || ''} ${s.last_name || ''}`.trim();

// Worst first when sorting descending: gaps (more gaps = worse), then on hold, then compliant,
// then roles with no requirements.
const complianceSeverity = (s) => {
  const g = Number(s.compliance_gaps) || 0;
  if (g > 0) return 1000 + g;
  if (s.on_hold) return 500;
  if (s.compliance_required != null && Number(s.compliance_required) === 0) return -1;
  return 0;
};

const fmtCap = (v) => { const n = Number(v); return num(n, Number.isInteger(n) ? 0 : 1); };
const hourCapText = (s) => {
  const parts = [];
  if (s.daily_hours_cap) parts.push(`${fmtCap(s.daily_hours_cap)} h/day`);
  if (s.weekly_hours_cap) parts.push(`${fmtCap(s.weekly_hours_cap)} h/week`);
  return parts.join(' · ') || '—';
};

// CSV of the rows given — same columns the export has always had.
function downloadCsv(rows, label) {
  const cols = ['first_name', 'last_name', 'email', 'phone', 'role', 'employment_type', 'job_title', 'is_active', 'created_at'];
  const esc = (v) => {
    const s = v == null ? '' : String(v).replace(/"/g, '""');
    return /[",\n]/.test(s) ? `"${s}"` : s;
  };
  const csv = [cols.join(','), ...rows.map((s) => cols.map((k) => esc(s[k])).join(','))].join('\n');
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const a = document.createElement('a');
  a.href = URL.createObjectURL(blob);
  a.download = `staff-${label}-${new Date().toISOString().slice(0, 10)}.csv`;
  document.body.appendChild(a); a.click(); a.remove();
  setTimeout(() => URL.revokeObjectURL(a.href), 1000);
}

/* ─── Small pieces ───────────────────────────────────────────────────────── */

const MenuTrigger = (label, Icon) => function Trigger() {
  return (
    <span className="inline-flex items-center gap-1.5 text-[13px] font-medium">
      {Icon && <Icon size={14} aria-hidden="true" />}{label}<ChevronDown size={13} aria-hidden="true" />
    </span>
  );
};
const ExportTrigger = MenuTrigger('Export', Download);
const ColumnsTrigger = MenuTrigger('Columns', SlidersHorizontal);
const TRIGGER_CLS = 'btn-secondary btn-sm !text-slate-700';

// A KPI tile that is also a filter: pressing it narrows the list and shows a removable chip.
function FilterTile({ on, onClick, ...tile }) {
  return (
    <button type="button" onClick={onClick} aria-pressed={on}
      className={clsx('block w-full rounded-xl text-left transition-shadow duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-400',
        on && 'ring-2 ring-primary-300')}>
      <KpiTile {...tile} />
    </button>
  );
}

function NameCell({ s }) {
  const gaps = Number(s.compliance_gaps) || 0;
  return (
    <div className="flex items-center gap-3 min-w-0">
      <PersonAvatar id={s.id} photo={s.profile_photo_url} firstName={s.first_name} lastName={s.last_name} size="sm" />
      <div className="min-w-0">
        <Link to={`/staff/${s.id}`} className="block truncate text-[13px] font-semibold text-slate-900 hover:text-primary-700 hover:underline">
          {fullName(s) || 'Unnamed'}
        </Link>
        <div className="mt-0.5 flex flex-wrap items-center gap-1">
          {!s.is_active && <StatusPill status="inactive" />}
          {s.on_hold && <span className="badge-yellow whitespace-nowrap" title="Excluded from rostering">On hold</span>}
          {gaps > 0 && <span className="badge-red whitespace-nowrap" title="Missing or expired required qualifications">{plural(gaps, 'compliance gap')}</span>}
          {s.is_active && !s.on_hold && gaps === 0 && s.job_title && <span className="truncate text-[12px] text-slate-500">{s.job_title}</span>}
        </div>
      </div>
    </div>
  );
}

/* ─── Page ───────────────────────────────────────────────────────────────── */

export default function StaffList() {
  // Payroll shortcuts show only when that software is connected (Settings › Integrations).
  const connected = useConnectedIntegrations();
  const [staff, setStaff]         = useState([]);
  const [loading, setLoading]     = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [search, setSearch]       = useState('');
  const [status, setStatus]       = useUrlTab('status', 'all', ['all', 'active', 'inactive']);
  const [focus, setFocus]         = useState(null);     // null | 'gaps' | 'on_hold' | 'no_docs'
  const [joinedFrom, setJoinedFrom] = useState('');
  const [joinedTo, setJoinedTo]     = useState('');
  const [sort, setSort]           = useState({ key: 'name', dir: 'asc' });
  const [pageSize, setPageSize]   = useState(25);
  const [page, setPage]           = useState(1);
  const [selected, setSelected]   = useState([]);
  const [columns, setColumns]     = useState(readColumns);

  const [burnoutOpen, setBurnoutOpen]       = useState(false);
  const [burnoutData, setBurnoutData]       = useState(null);
  const [burnoutError, setBurnoutError]     = useState(null);
  const [loadingBurnout, setLoadingBurnout] = useState(false);

  const [exportOpen, setExportOpen] = useState(false);

  /* ── data ── */
  const load = useCallback(() => {
    setLoading(true);
    setLoadError(null);
    api.get('/staff?is_active=all')
      .then((r) => setStaff(Array.isArray(r.data) ? r.data : (r.data?.staff || [])))
      .catch((e) => setLoadError(e.response?.data?.message || "We couldn't load your staff. Check your connection, then try again."))
      .finally(() => setLoading(false));
  }, []);
  useEffect(() => { load(); }, [load]);

  useEffect(() => { try { localStorage.setItem(COLS_KEY, JSON.stringify(columns)); } catch { /* ignore */ } }, [columns]);

  /* ── KPIs (active staff only: those are the people who can be rostered) ── */
  const kpi = useMemo(() => {
    const active = staff.filter((s) => s.is_active);
    const hasDocCounts = staff.some((s) => s.document_count != null);
    return {
      all: staff.length,
      active: active.length,
      inactive: staff.length - active.length,
      gaps: active.filter((s) => Number(s.compliance_gaps) > 0).length,
      onHold: active.filter((s) => s.on_hold).length,
      noDocs: hasDocCounts ? active.filter((s) => Number(s.document_count) === 0).length : null,
    };
  }, [staff]);

  /* ── filtering ── */
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return staff.filter((s) => {
      if (q && !`${s.first_name} ${s.last_name} ${s.email || ''} ${s.phone || ''} ${s.job_title || ''}`.toLowerCase().includes(q)) return false;
      if (status === 'active' && !s.is_active) return false;
      if (status === 'inactive' && s.is_active) return false;
      if (focus === 'gaps' && !(s.is_active && Number(s.compliance_gaps) > 0)) return false;
      if (focus === 'on_hold' && !(s.is_active && s.on_hold)) return false;
      if (focus === 'no_docs' && !(s.is_active && Number(s.document_count) === 0)) return false;
      // "Joined" is the worker's start date — the date the column shows — not when the record was made.
      const start = String(s.start_date || '').slice(0, 10);
      if (joinedFrom && (!start || start < joinedFrom)) return false;
      if (joinedTo && (!start || start > joinedTo)) return false;
      return true;
    });
  }, [staff, search, status, focus, joinedFrom, joinedTo]);

  /* ── sorting (whole list, before paging) ── */
  const SORT_VALUE = useMemo(() => ({
    name: (s) => fullName(s).toLowerCase(),
    role: (s) => roleLabel(s.role),
    employment: (s) => (s.employment_type ? employmentLabel(s.employment_type) : null),
    team: (s) => s.team_names || null,
    mobile: (s) => s.phone || null,
    compliance: complianceSeverity,
    last_login: (s) => (s.last_login_at ? new Date(s.last_login_at).getTime() : null),
    start_date: (s) => (s.start_date ? String(s.start_date).slice(0, 10) : null),
    job_title: (s) => s.job_title || null,
    email: (s) => (s.email || '').toLowerCase() || null,
    gender: (s) => s.gender || null,
    created: (s) => (s.created_at ? new Date(s.created_at).getTime() : null),
    hour_cap: (s) => (s.weekly_hours_cap != null ? Number(s.weekly_hours_cap) : (s.daily_hours_cap != null ? Number(s.daily_hours_cap) : null)),
  }), []);

  const sorted = useMemo(() => {
    const read = SORT_VALUE[sort.key] || SORT_VALUE.name;
    const dir = sort.dir === 'desc' ? -1 : 1;
    return [...filtered].sort((a, b) => {
      const x = read(a); const y = read(b);
      if (x == null && y == null) return 0;
      if (x == null) return 1;
      if (y == null) return -1;
      if (typeof x === 'number' && typeof y === 'number') return (x - y) * dir;
      return String(x).localeCompare(String(y), 'en-AU', { numeric: true, sensitivity: 'base' }) * dir;
    });
  }, [filtered, sort, SORT_VALUE]);

  /* ── paging ── */
  const total      = sorted.length;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const safePage   = Math.min(page, totalPages);
  const from       = total === 0 ? 0 : (safePage - 1) * pageSize + 1;
  const to         = Math.min(safePage * pageSize, total);
  const pageRows   = sorted.slice(from > 0 ? from - 1 : 0, to);
  useEffect(() => { setPage(1); }, [search, status, focus, joinedFrom, joinedTo, pageSize]);

  const selectedRows = useMemo(() => staff.filter((s) => selected.includes(s.id)), [staff, selected]);

  /* ── actions ── */
  const clearFilters = () => { setSearch(''); setStatus('all'); setFocus(null); setJoinedFrom(''); setJoinedTo(''); };

  const bulkEmail = () => {
    const emails = selectedRows.map((s) => s.email).filter(Boolean);
    if (!emails.length) { toast.error('None of the selected staff have an email address'); return; }
    window.location.href = `mailto:?bcc=${emails.join(',')}`;
    toast.success(`Opening your email app for ${plural(emails.length, 'person', 'people')}`);
  };
  const bulkSms = () => {
    const phones = selectedRows.map((s) => s.phone).filter(Boolean);
    if (!phones.length) { toast.error('None of the selected staff have a mobile number'); return; }
    window.location.href = `sms:${phones.join(',')}`;
    toast.success(`Opening messages for ${plural(phones.length, 'person', 'people')}`);
  };
  const exportSelected = () => { downloadCsv(selectedRows, 'selected'); toast.success(`Exported ${plural(selectedRows.length, 'staff member')} to CSV`); };
  const exportView = () => {
    if (!sorted.length) { toast.error('Nothing to export: no staff match your filters'); return; }
    downloadCsv(sorted, 'view');
    toast.success(`Exported ${plural(sorted.length, 'staff member')} to CSV`);
  };

  const runBurnoutScan = async () => {
    setBurnoutOpen(true);
    setBurnoutData(null);
    setBurnoutError(null);
    setLoadingBurnout(true);
    try {
      const { data } = await api.post('/ai/staff-burnout');
      setBurnoutData(data);
    } catch (e) {
      setBurnoutError(e.response?.data?.message || "The workload scan didn't finish. Try again in a moment.");
    } finally {
      setLoadingBurnout(false);
    }
  };
  const closeBurnout = useStableCallback(() => setBurnoutOpen(false));

  const toggleColumn = (key) => setColumns((c) => (c.includes(key) ? c.filter((k) => k !== key) : [...c, key]));

  /* ── table columns ── */
  const allColumns = {
    role: { key: 'role', header: 'Role', sortable: true, render: (s) => <span className="whitespace-nowrap">{roleLabel(s.role)}</span> },
    employment: { key: 'employment', header: 'Employment', sortable: true, render: (s) => <span className="whitespace-nowrap">{s.employment_type ? employmentLabel(s.employment_type) : <span className="text-slate-400">—</span>}</span> },
    team: { key: 'team', header: 'Team', sortable: true, className: 'max-w-[200px]', render: (s) => (s.team_names ? <span className="block truncate" title={s.team_names}>{s.team_names}</span> : <span className="text-slate-400">—</span>) },
    mobile: { key: 'mobile', header: 'Mobile', sortable: true, render: (s) => (s.phone ? <a href={`tel:${s.phone}`} className="whitespace-nowrap tabular-nums hover:text-primary-700 hover:underline">{s.phone}</a> : <span className="text-slate-400">—</span>) },
    compliance: { key: 'compliance', header: 'Compliance', sortable: true, defaultDir: 'desc', render: (s) => <ComplianceBadge gaps={s.compliance_gaps} onHold={s.on_hold} required={s.compliance_required} /> },
    last_login: { key: 'last_login', header: 'Last login', sortable: true, defaultDir: 'desc', render: (s) => <span className="whitespace-nowrap text-slate-600">{s.last_login_at ? fmtAgo(s.last_login_at) : 'Never'}</span> },
    start_date: { key: 'start_date', header: 'Start date', sortable: true, render: (s) => <span className="whitespace-nowrap tabular-nums">{s.start_date ? fmtDate(String(s.start_date).slice(0, 10)) : <span className="text-slate-400">—</span>}</span> },
    job_title: { key: 'job_title', header: 'Job title', sortable: true, render: (s) => <span className="whitespace-nowrap">{s.job_title || <span className="text-slate-400">—</span>}</span> },
    email: { key: 'email', header: 'Email', sortable: true, className: 'max-w-[220px]', render: (s) => (s.email ? <a href={`mailto:${s.email}`} className="block truncate hover:text-primary-700 hover:underline" title={s.email}>{s.email}</a> : <span className="text-slate-400">—</span>) },
    gender: { key: 'gender', header: 'Gender', sortable: true, render: (s) => (s.gender ? genderLabel(s.gender) : <span className="text-slate-400">—</span>) },
    created: { key: 'created', header: 'Created', sortable: true, defaultDir: 'desc', render: (s) => <span className="whitespace-nowrap tabular-nums">{s.created_at ? fmtDate(s.created_at) : '—'}</span> },
    hour_cap: { key: 'hour_cap', header: 'Hour cap', sortable: true, align: 'right', render: (s) => <span className="whitespace-nowrap tabular-nums">{hourCapText(s)}</span> },
  };
  const tableColumns = [
    { key: 'name', header: 'Name', sortable: true, render: (s) => <NameCell s={s} /> },
    ...PICKABLE.filter((p) => columns.includes(p.key)).map((p) => allColumns[p.key]),
  ];

  const statusTabs = [
    { id: 'all', label: 'All', count: kpi.all },
    { id: 'active', label: 'Active', count: kpi.active },
    { id: 'inactive', label: 'Inactive', count: kpi.inactive },
  ];

  const FOCUS_LABEL = { gaps: 'Compliance gaps', on_hold: 'On hold', no_docs: 'No documents' };
  const toggleFocus = (f) => setFocus((cur) => (cur === f ? null : f));

  const empty = staff.length === 0
    ? { icon: Users, title: 'No staff yet', hint: 'Add your team so you can roster them, track their checks and pay them.', action: <Link to="/staff/new" className="btn-primary btn-sm"><Plus size={14} />Add staff</Link> }
    : { icon: Users, title: 'No staff match your filters', hint: 'Try a different search, or clear the filters to see everyone.', action: <button type="button" onClick={clearFilters} className="btn-secondary btn-sm">Clear filters</button> };

  /* ─────────────────────────────────────────────────────────────────────── */
  return (
    <div className={PAGE_WIDE}>

      {/* ── KPI tiles double as filters ── */}
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <FilterTile on={status === 'active' && !focus} onClick={() => { setFocus(null); setStatus(status === 'active' ? 'all' : 'active'); }}
          label="Active staff" value={loading ? '…' : num(kpi.active)} accent="emerald" icon={Users}
          sub={loading ? ' ' : `${num(kpi.inactive)} inactive`} />
        <FilterTile on={focus === 'gaps'} onClick={() => toggleFocus('gaps')}
          label="Compliance gaps" value={loading ? '…' : num(kpi.gaps)} alert={!loading && kpi.gaps > 0} accent="slate" icon={ShieldAlert}
          sub="Active staff missing a required check" />
        <FilterTile on={focus === 'on_hold'} onClick={() => toggleFocus('on_hold')}
          label="On hold" value={loading ? '…' : num(kpi.onHold)} accent="amber" icon={PauseCircle}
          sub="Excluded from rostering" />
        <FilterTile on={focus === 'no_docs'} onClick={() => toggleFocus('no_docs')}
          label="No documents" value={loading ? '…' : (kpi.noDocs == null ? '—' : num(kpi.noDocs))} accent="slate" icon={FileX}
          sub="Active staff with nothing uploaded" />
      </div>

      {/* ── Toolbar ── */}
      <div className="rounded-xl border border-slate-200 bg-white">
        <div className="flex flex-col gap-3 px-4 py-3 lg:flex-row lg:flex-wrap lg:items-center">
          <SearchInput value={search} onChange={setSearch} placeholder="Search name, email or phone" className="w-full lg:w-64" />
          <Tabs variant="segmented" size="sm" ariaLabel="Staff status" tabs={statusTabs} value={status} onChange={setStatus} className="self-start" />
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-[12.5px] font-medium text-slate-600">Joined</span>
            <input type="date" aria-label="Joined from" value={joinedFrom} max={joinedTo || undefined}
              onChange={(e) => setJoinedFrom(e.target.value)} className="h-8 rounded-lg border border-slate-200 bg-white px-2 text-[13px] text-slate-700" />
            <span className="text-slate-400" aria-hidden="true">–</span>
            <input type="date" aria-label="Joined to" value={joinedTo} min={joinedFrom || undefined}
              onChange={(e) => setJoinedTo(e.target.value)} className="h-8 rounded-lg border border-slate-200 bg-white px-2 text-[13px] text-slate-700" />
          </div>
          <div className="flex flex-wrap items-center gap-2 lg:ml-auto">
            <button type="button" onClick={runBurnoutScan} disabled={loadingBurnout} className="btn-secondary btn-sm">
              <Sparkles size={14} className="text-slate-500" aria-hidden="true" />{loadingBurnout ? 'Scanning…' : 'Burnout scan'}
            </button>
            <OverflowMenu label="Export staff" icon={ExportTrigger} className={TRIGGER_CLS} items={[
              { label: 'Excel — all staff, every field', icon: FileSpreadsheet, onClick: () => setExportOpen(true) },
              { label: `CSV — this view (${num(sorted.length)})`, icon: Download, onClick: exportView, disabled: !sorted.length },
            ]} />
            <OverflowMenu label="Choose columns" icon={ColumnsTrigger} className={TRIGGER_CLS} items={PICKABLE.map((p) => ({
              label: p.label, icon: columns.includes(p.key) ? CheckSquare : Square, onClick: () => toggleColumn(p.key),
            }))} />
            {connected.employmentHero && (
              <Link to="/timesheets?tab=employment-hero&view=staff" className="btn-secondary btn-sm" title="Create Employment Hero employees for staff who are not there yet">Add to Employment Hero</Link>
            )}
            {connected.xero && (
              <Link to="/timesheets?tab=xero&view=staff" className="btn-secondary btn-sm" title="Create Xero Payroll employees for staff who are not there yet">Add to Xero</Link>
            )}
            {connected.quickbooks && (
              <Link to="/timesheets?tab=quickbooks&view=staff" className="btn-secondary btn-sm" title="Create QuickBooks employees for staff who are not there yet">Add to QuickBooks</Link>
            )}
            <Link to="/staff/new" className="btn-primary btn-sm"><Plus size={14} aria-hidden="true" />Add staff</Link>
          </div>
        </div>

        {/* Applied filters, each removable */}
        {(focus || joinedFrom || joinedTo) && (
          <div className="flex flex-wrap items-center gap-2 border-t border-slate-100 px-4 py-2">
            {focus && (
              <button type="button" onClick={() => setFocus(null)} className="inline-flex h-7 items-center gap-1.5 rounded-full border border-primary-300 bg-primary-50 pl-2.5 pr-2 text-[12px] font-medium text-primary-700 hover:bg-primary-100">
                {FOCUS_LABEL[focus]}<X size={12} aria-hidden="true" /><span className="sr-only">Remove filter</span>
              </button>
            )}
            {(joinedFrom || joinedTo) && (
              <button type="button" onClick={() => { setJoinedFrom(''); setJoinedTo(''); }} className="inline-flex h-7 items-center gap-1.5 rounded-full border border-primary-300 bg-primary-50 pl-2.5 pr-2 text-[12px] font-medium text-primary-700 hover:bg-primary-100">
                Joined {joinedFrom ? fmtDate(joinedFrom) : 'any time'} – {joinedTo ? fmtDate(joinedTo) : 'today'}<X size={12} aria-hidden="true" /><span className="sr-only">Remove filter</span>
              </button>
            )}
            <button type="button" onClick={clearFilters} className="text-[12px] font-medium text-slate-500 hover:text-slate-800 hover:underline">Clear all</button>
          </div>
        )}
      </div>

      {/* ── Register ── */}
      <div className="overflow-hidden rounded-xl border border-slate-200 bg-white">
        {/* Phones: one card per worker, with a tap-to-call number */}
        <div className="sm:hidden">
          {loading ? <TableSkeleton rows={6} cols={2} />
            : loadError ? <ErrorState message={loadError} onRetry={load} />
              : pageRows.length === 0 ? <EmptyState {...empty} />
                : (
                  <ul className="divide-y divide-slate-100">
                    {pageRows.map((s) => (
                      <li key={s.id} className="flex items-start gap-3 px-4 py-3">
                        <PersonAvatar id={s.id} photo={s.profile_photo_url} firstName={s.first_name} lastName={s.last_name} size="md" />
                        <div className="min-w-0 flex-1">
                          <Link to={`/staff/${s.id}`} className="block truncate text-sm font-semibold text-slate-900 hover:underline">{fullName(s) || 'Unnamed'}</Link>
                          <p className="truncate text-[12.5px] text-slate-500">{[roleLabel(s.role), s.employment_type && employmentLabel(s.employment_type)].filter(Boolean).join(' · ')}</p>
                          <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
                            {!s.is_active && <StatusPill status="inactive" />}
                            <ComplianceBadge gaps={s.compliance_gaps} onHold={s.on_hold} required={s.compliance_required} />
                          </div>
                        </div>
                        {s.phone && (
                          <a href={`tel:${s.phone}`} aria-label={`Call ${fullName(s)}`} className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-lg border border-slate-200 text-slate-600 hover:bg-slate-50">
                            <Phone size={16} aria-hidden="true" />
                          </a>
                        )}
                      </li>
                    ))}
                  </ul>
                )}
        </div>

        {/* Wider screens: the register */}
        <div className="hidden sm:block">
          <DataTable
            label="Staff register"
            columns={tableColumns}
            rows={pageRows}
            loading={loading}
            error={loadError}
            onRetry={load}
            empty={empty}
            sort={sort}
            onSortChange={setSort}
            selectable
            selected={selected}
            onSelectedChange={setSelected}
          />
        </div>

        {/* Paging */}
        {!loading && !loadError && total > 0 && (
          <div className="flex flex-wrap items-center justify-between gap-3 border-t border-slate-100 px-4 py-3 text-[13px] text-slate-600">
            <div className="flex flex-wrap items-center gap-2">
              <span className="tabular-nums">{num(from)}–{num(to)} of {plural(total, 'staff member')}</span>
              <label className="inline-flex items-center gap-1.5">
                <span className="text-slate-500">Rows per page</span>
                <select value={pageSize} onChange={(e) => setPageSize(Number(e.target.value))} className="h-8 rounded-lg border border-slate-200 bg-white px-2 text-[13px]">
                  {[10, 25, 50, 100].map((n) => <option key={n} value={n}>{n}</option>)}
                </select>
              </label>
            </div>
            <div className="flex items-center gap-1">
              <button type="button" onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={safePage === 1} className="btn-secondary btn-sm" aria-label="Previous page">
                <ChevronLeft size={14} aria-hidden="true" />
              </button>
              {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                const pg = totalPages <= 5 ? i + 1 : Math.min(Math.max(1, safePage - 2), totalPages - 4) + i;
                return (
                  <button key={pg} type="button" onClick={() => setPage(pg)} aria-current={pg === safePage ? 'page' : undefined}
                    className={clsx('h-8 min-w-[2rem] rounded-lg px-2 text-[13px] font-medium tabular-nums transition-colors',
                      pg === safePage ? 'bg-primary-600 text-white' : 'border border-slate-200 text-slate-600 hover:bg-slate-50')}>
                    {pg}
                  </button>
                );
              })}
              <button type="button" onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={safePage === totalPages} className="btn-secondary btn-sm" aria-label="Next page">
                <ChevronRight size={14} aria-hidden="true" />
              </button>
            </div>
          </div>
        )}
      </div>

      {/* ── Bulk actions: only when rows are selected, and only for those rows ── */}
      <BulkBar count={selected.length} noun={selected.length === 1 ? 'staff member selected' : 'staff selected'} onClear={() => setSelected([])}>
        <button type="button" onClick={bulkEmail} className="btn-secondary btn-sm"><Mail size={13} aria-hidden="true" />Email</button>
        <button type="button" onClick={bulkSms} className="btn-secondary btn-sm"><MessageSquare size={13} aria-hidden="true" />SMS</button>
        <button type="button" onClick={exportSelected} className="btn-secondary btn-sm"><Download size={13} aria-hidden="true" />Export CSV</button>
      </BulkBar>

      {/* ── Excel export: password asked for in a dialog ── */}
      <ExcelExportDialog open={exportOpen} onClose={() => setExportOpen(false)} />

      {/* ── Burnout scan ── */}
      <Dialog open={burnoutOpen} onClose={closeBurnout} size="lg" title="Burnout scan"
        description="Hours, cancellations, leave and incidents over the last 30 days."
        footer={<button type="button" onClick={closeBurnout} className="btn-secondary">Close</button>}>
        {loadingBurnout && (
          <div className="space-y-3" aria-busy="true" aria-label="Scanning">
            <p className="text-sm text-slate-500">Looking at everyone's workload…</p>
            <TableSkeleton rows={3} cols={2} />
          </div>
        )}
        {!loadingBurnout && burnoutError && <ErrorState message={burnoutError} onRetry={runBurnoutScan} />}
        {!loadingBurnout && !burnoutError && burnoutData?.atRisk?.length === 0 && (
          <EmptyState icon={CheckCircle2} title="Everyone is within a healthy workload" hint="No burnout risk found in the last 30 days." />
        )}
        {!loadingBurnout && !burnoutError && burnoutData?.atRisk?.length > 0 && (
          <ul className="space-y-3">
            {burnoutData.atRisk.map((s, i) => {
              const high = s.riskLevel === 'high';
              return (
                <li key={`${s.staffName}-${i}`} className="rounded-xl border border-slate-200 p-4">
                  <div className="flex flex-wrap items-center gap-2">
                    <AlertTriangle size={14} className={high ? 'text-rose-600' : 'text-amber-600'} aria-hidden="true" />
                    <p className="text-sm font-semibold text-slate-900">{s.staffName}</p>
                    <span className={high ? 'badge-red' : 'badge-yellow'}>{high ? 'High' : 'Moderate'} risk · score {s.score}</span>
                  </div>
                  {s.signals?.length > 0 && (
                    <div className="mt-2 flex flex-wrap gap-1">
                      {s.signals.map((sig, j) => <span key={j} className="badge-gray">{sig}</span>)}
                    </div>
                  )}
                  {s.recommendation && (
                    <p className="mt-2 text-[13px] text-slate-700"><span className="font-medium">Recommended: </span>{s.recommendation}</p>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </Dialog>
    </div>
  );
}

/* ─── Excel export dialog ────────────────────────────────────────────────── */

function ExcelExportDialog({ open, onClose }) {
  const [pw, setPw] = useState('');
  const [pw2, setPw2] = useState('');
  const [show, setShow] = useState(false);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) { setPw(''); setPw2(''); setError(''); setShow(false); } }, [open]);
  const close = useStableCallback(() => { if (!busy) onClose(); });

  const submit = async (e) => {
    e.preventDefault();
    if (pw.trim().length < 6) { setError('Use at least 6 characters.'); return; }
    if (pw !== pw2) { setError("The two passwords don't match."); return; }
    setBusy(true);
    setError('');
    try {
      const res = await api.get('/export/staff?format=xlsx', { responseType: 'blob', headers: { 'X-Export-Password': pw } });
      const disposition = res.headers?.['content-disposition'];
      const match = disposition && /filename="?([^"';]+)"?/i.exec(disposition);
      const name = (match && match[1]) || `staff-${new Date().toISOString().slice(0, 10)}.xlsx`;
      const blob = res.data instanceof Blob ? res.data : new Blob([res.data]);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = name;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
      toast.success("Exported all staff to Excel. You'll need the password to open the file.");
      onClose();
    } catch (err) {
      setError(err.response?.data?.message || "The export didn't finish. Try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onClose={close} size="sm" title="Export all staff to Excel"
      description="Every field for every staff member, including contact and pay details. The file is locked with a password you choose now."
      footer={(
        <>
          <button type="button" onClick={close} className="btn-secondary" disabled={busy}>Cancel</button>
          <button type="submit" form="staff-excel-export" className="btn-primary" disabled={busy}>{busy ? 'Exporting…' : 'Export'}</button>
        </>
      )}>
      <form id="staff-excel-export" onSubmit={submit} className="space-y-4">
        <Field label="Password" required hint="At least 6 characters. Share it separately from the file.">
          {(id) => (
            <div className="relative">
              <input id={id} type={show ? 'text' : 'password'} autoComplete="new-password" className="input-field pr-10" value={pw}
                onChange={(e) => { setPw(e.target.value); setError(''); }} />
              <button type="button" onClick={() => setShow((v) => !v)} aria-label={show ? 'Hide password' : 'Show password'}
                className="absolute right-1.5 top-1/2 inline-flex h-8 w-8 -translate-y-1/2 items-center justify-center rounded-lg text-slate-500 hover:bg-slate-100">
                {show ? <EyeOff size={15} aria-hidden="true" /> : <Eye size={15} aria-hidden="true" />}
              </button>
            </div>
          )}
        </Field>
        <Field label="Confirm password" required error={error}>
          {(id) => (
            <input id={id} type={show ? 'text' : 'password'} autoComplete="new-password" className="input-field" value={pw2}
              onChange={(e) => { setPw2(e.target.value); setError(''); }} />
          )}
        </Field>
      </form>
    </Dialog>
  );
}
